/**
 * Command palette ranking: every query token must match the title or a keyword. Word-prefix
 * matches beat substrings, which beat in-order subsequences ("adlg" → "Audit log"). Ties keep
 * the caller's order, so navigation lists stay in their designed sequence.
 */

export interface CommandLike {
  id: string
  title: string
  keywords?: readonly string[]
  group?: string
}

export interface Ranked<C extends CommandLike> {
  command: C
  score: number
  /** [start, end) ranges in `title` to emphasise. */
  ranges: [number, number][]
}

interface TokenMatch {
  score: number
  range: [number, number] | null
}

function wordStarts(text: string): number[] {
  const starts: number[] = []
  for (let i = 0; i < text.length; i++) {
    const prev = text[i - 1]
    if (i === 0 || prev === ' ' || prev === '-' || prev === '_' || prev === '/' || prev === '·' || prev === '.' || prev === ':') {
      starts.push(i)
    }
  }
  return starts
}

function matchToken(token: string, text: string, inTitle: boolean): TokenMatch | null {
  const hay = text.toLowerCase()
  if (!token) return { score: 0, range: null }
  const weight = inTitle ? 1 : 0.7
  if (hay === token) return { score: 120 * weight, range: inTitle ? [0, token.length] : null }
  for (const s of wordStarts(hay)) {
    if (hay.startsWith(token, s)) {
      return { score: (s === 0 ? 100 : 80) * weight, range: inTitle ? [s, s + token.length] : null }
    }
  }
  const at = hay.indexOf(token)
  if (at >= 0) return { score: 50 * weight, range: inTitle ? [at, at + token.length] : null }
  // in-order subsequence, rewarded for compactness
  let j = 0
  let first = -1
  let last = -1
  for (let i = 0; i < hay.length && j < token.length; i++) {
    if (hay[i] === token[j]) {
      if (first < 0) first = i
      last = i
      j++
    }
  }
  if (j === token.length && token.length >= 2) {
    const spread = last - first + 1
    return { score: Math.max(5, 30 - (spread - token.length) * 2) * weight, range: null }
  }
  return null
}

export function tokenize(query: string): string[] {
  return query.trim().toLowerCase().split(/\s+/).filter(Boolean)
}

/** Ranks commands for a query. Empty query returns everything in the given order. */
export function rankCommands<C extends CommandLike>(query: string, commands: readonly C[]): Ranked<C>[] {
  const tokens = tokenize(query)
  if (tokens.length === 0) return commands.map((command) => ({ command, score: 0, ranges: [] }))
  const out: (Ranked<C> & { order: number })[] = []
  commands.forEach((command, order) => {
    let total = 0
    const ranges: [number, number][] = []
    for (const token of tokens) {
      let best = matchToken(token, command.title, true)
      for (const k of command.keywords ?? []) {
        const m = matchToken(token, k, false)
        if (m && (!best || m.score > best.score)) best = m
      }
      if (!best) return
      total += best.score
      if (best.range) ranges.push(best.range)
    }
    out.push({ command, score: total / tokens.length, ranges: mergeRanges(ranges), order })
  })
  out.sort((a, b) => b.score - a.score || a.order - b.order)
  return out.map(({ command, score, ranges }) => ({ command, score, ranges }))
}

export function mergeRanges(ranges: [number, number][]): [number, number][] {
  const sorted = [...ranges].sort((a, b) => a[0] - b[0])
  const merged: [number, number][] = []
  for (const r of sorted) {
    const last = merged[merged.length - 1]
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1])
    else merged.push([r[0], r[1]])
  }
  return merged
}

/** Splits `title` into plain and emphasised parts for rendering. */
export function highlight(title: string, ranges: [number, number][]): { text: string; hit: boolean }[] {
  const parts: { text: string; hit: boolean }[] = []
  let at = 0
  for (const [s, e] of ranges) {
    if (s > at) parts.push({ text: title.slice(at, s), hit: false })
    parts.push({ text: title.slice(s, e), hit: true })
    at = e
  }
  if (at < title.length) parts.push({ text: title.slice(at), hit: false })
  return parts
}
